/**
 * Drag-to-scroll for Sub Collection List horizontal track.
 */
(function () {
  const DRAG_THRESHOLD = 4;
  const MOMENTUM_DECAY = 0.92;
  const MIN_VELOCITY = 0.35;

  /**
   * @param {HTMLElement} section
   */
  function initCollectionSubList(section) {
    const track = section.querySelector('[data-collection-sub-list-track]');
    if (!track || track.dataset.dragInit === 'true') return;

    track.dataset.dragInit = 'true';

    let isPointerDown = false;
    let hasDragged = false;
    let suppressClick = false;
    let startX = 0;
    let startScrollLeft = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    /** @type {number | null} */
    let momentumFrame = null;

    const canScroll = () => !track.hasAttribute('data-fits');

    const cancelMomentum = () => {
      if (momentumFrame !== null) {
        cancelAnimationFrame(momentumFrame);
        momentumFrame = null;
      }
    };

    const endDrag = () => {
      isPointerDown = false;
      track.classList.remove('is-dragging');
    };

    const resetPointerState = () => {
      isPointerDown = false;
      hasDragged = false;
      track.classList.remove('is-dragging');
    };

    const getSnapPositions = () => {
      const items = track.querySelectorAll('.collection-sub-list__item');
      return Array.from(items, (item) => item.offsetLeft);
    };

    /**
     * @param {boolean} smooth
     */
    const snapToNearest = (smooth = true) => {
      const positions = getSnapPositions();
      if (!positions.length) return;

      const scroll = track.scrollLeft;
      let nearest = positions[0];
      let minDistance = Math.abs(scroll - nearest);

      for (const position of positions) {
        const distance = Math.abs(scroll - position);
        if (distance < minDistance) {
          minDistance = distance;
          nearest = position;
        }
      }

      track.scrollTo({
        left: nearest,
        behavior: smooth ? 'smooth' : 'auto',
      });
    };

    const runMomentum = () => {
      cancelMomentum();
      let momentum = velocity;

      const step = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;

        if (Math.abs(momentum) < MIN_VELOCITY) {
          momentumFrame = null;
          snapToNearest(true);
          return;
        }

        track.scrollLeft -= momentum;
        momentum *= MOMENTUM_DECAY;

        if (track.scrollLeft <= 0 || track.scrollLeft >= maxScroll) {
          momentumFrame = null;
          snapToNearest(true);
          return;
        }

        momentumFrame = requestAnimationFrame(step);
      };

      momentumFrame = requestAnimationFrame(step);
    };

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (!canScroll()) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        cancelMomentum();
        isPointerDown = true;
        hasDragged = false;
        suppressClick = false;
        velocity = 0;
        startX = event.clientX;
        lastX = event.clientX;
        lastTime = performance.now();
        startScrollLeft = track.scrollLeft;
      },
      true
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!isPointerDown || !canScroll()) return;

        const now = performance.now();
        const currentX = event.clientX;
        const deltaFromStart = currentX - startX;

        if (!hasDragged && Math.abs(deltaFromStart) < DRAG_THRESHOLD) return;

        if (!hasDragged) {
          hasDragged = true;
          track.classList.add('is-dragging');
          track.setPointerCapture?.(event.pointerId);
        }

        const elapsed = now - lastTime;
        if (elapsed > 0) {
          velocity = ((currentX - lastX) / elapsed) * 16;
        }

        lastX = currentX;
        lastTime = now;

        event.preventDefault();
        track.scrollLeft = startScrollLeft - deltaFromStart;
      },
      true
    );

    const onPointerEnd = (event) => {
      if (!isPointerDown) return;

      const didDrag = hasDragged;

      if (didDrag) {
        suppressClick = true;
        event.preventDefault();
        track.releasePointerCapture?.(event.pointerId);
        endDrag();

        if (Math.abs(velocity) >= MIN_VELOCITY) {
          runMomentum();
        } else {
          snapToNearest(true);
        }
      } else {
        resetPointerState();
      }
    };

    track.addEventListener('pointerup', onPointerEnd, true);
    track.addEventListener('pointercancel', onPointerEnd, true);

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      },
      true
    );

    track.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    const updateCentered = () => {
      cancelMomentum();
      const fits = track.scrollWidth <= track.clientWidth + 1;
      track.toggleAttribute('data-fits', fits);

      if (fits) {
        endDrag();
      }
    };

    updateCentered();

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateCentered);
      observer.observe(track);
    } else {
      window.addEventListener('resize', updateCentered);
    }
  }

  function initAll() {
    document.querySelectorAll('.collection-sub-list').forEach(initCollectionSubList);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const section = target.classList.contains('collection-sub-list')
      ? target
      : target.querySelector('.collection-sub-list');

    if (section instanceof HTMLElement) {
      initCollectionSubList(section);
    }
  });
})();
